import React, { useContext, useEffect, useRef, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { MiwaAppWebsocketContext } from '@/contexts/miwa-app-websocket'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { fetchMiwaCardKeyReservation } from '@/apis/aipass'
import { MiwaLockWebsocketResponseEvent, MiwaLockWebsocketResponseStatusCode } from '@/constants/miwa-lock'
import { TMiwaAppWebsocketContext, TMiwaCardKeyReservation, TMiwaReadCardResult } from '@/models/miwa-lock'
import { parseReadCardResult, toCardDate, toMiwaAppErrorMessage, TReadCard } from '@/utils/miwa-lock'
import { toReservationDetailLocation } from '@/utils/reservations'
import { CardKeyConnectModal } from './card-key-connect-modal'
import { CardKeyInfoModal } from './card-key-info-modal'
import { CardKeyReadErrorModal } from './card-key-read-error-modal'

type ReadCardState = { card: TReadCard; reservation: TMiwaCardKeyReservation | null }

/**
 * ヘッダーに置く美和ロック発行機の接続状態と、カードをかざしたときの読取り結果の表示
 * 読取りはどの画面からでも行えるよう、全画面に出るヘッダーで受け取る
 */
export const CardKeyConnection: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { isMiwaAppConnected, lastJsonMessage } = useContext<TMiwaAppWebsocketContext>(MiwaAppWebsocketContext)
  const [isOpenConnectModal, setIsOpenConnectModal] = useState(false)
  const [readCard, setReadCard] = useState<ReadCardState | null>(null)
  const [readErrorMessage, setReadErrorMessage] = useState('')
  // lastJsonMessage はアプリ全体の Provider が保持し続ける一方、Header はページごとに再マウントされる。
  // マウント時点で残っているメッセージを処理済みとして扱い、遷移のたびに前回の読取り結果を再処理しない
  const handledMessageRef = useRef(lastJsonMessage)

  const onReadCard = async (body: TMiwaReadCardResult) => {
    const card = parseReadCardResult(body)
    if (!card) {
      setReadErrorMessage(
        body.status_code >= MiwaLockWebsocketResponseStatusCode.INVALID_DATA
          ? toMiwaAppErrorMessage(body.status_code, t)
          : t('No information is written on the card'),
      )
      return
    }

    try {
      const reservation = await fetchMiwaCardKeyReservation({
        roomNumber: card.roomNumber,
        checkinDate: toCardDate(card.startAt),
        checkoutDate: toCardDate(card.endAt),
      })
      setReadCard({ card, reservation })
    } catch (error) {
      // 紐づく部屋割がない（退室済みのカード等）場合もカードの情報は確認できるよう、予約なしで表示する
      if ((error as AxiosError)?.response?.status === 404) {
        setReadCard({ card, reservation: null })
        return
      }
      errorHandler(error)
    }
  }

  // 予約詳細はステータスごとに画面が分かれるため、客室管理のアサイン画面と同じ規則で遷移先を決める
  const reservationDetailLocation = readCard?.reservation ? toReservationDetailLocation(readCard.reservation) : null

  useEffect(() => {
    if (lastJsonMessage === handledMessageRef.current) return
    handledMessageRef.current = lastJsonMessage

    if (lastJsonMessage?.type === MiwaLockWebsocketResponseEvent.READ_CARD_RESULT) {
      onReadCard(lastJsonMessage.body)
    }
  }, [lastJsonMessage])

  return (
    <>
      <span css={headerDividerStyle} />
      <button type="button" css={statusStyle} onClick={() => setIsOpenConnectModal(true)}>
        {t('Card issuer')} :{' '}
        <span css={isMiwaAppConnected ? undefined : notConnectedStyle}>
          {isMiwaAppConnected ? t('Card issuer connected') : t('Card issuer not connected')}
        </span>
      </button>
      {isOpenConnectModal && <CardKeyConnectModal onClose={() => setIsOpenConnectModal(false)} />}
      {readCard && (
        <CardKeyInfoModal
          card={readCard.card}
          reservation={readCard.reservation}
          onClose={() => setReadCard(null)}
          reservationDetailLocation={reservationDetailLocation}
        />
      )}
      {readErrorMessage && <CardKeyReadErrorModal message={readErrorMessage} onClose={() => setReadErrorMessage('')} />}
    </>
  )
}

// 客室管理ヘッダーの「室料金」と同じ区切り線・文字サイズにして、ヘッダー内で縦位置と見た目を揃える
const headerDividerStyle = css({
  width: 1,
  height: 18,
  background: '#CCCCCC',
  margin: '0 16px',
  flexShrink: 0,
})

const statusStyle = css({
  color: '#676767',
  border: 'none',
  background: 'transparent',
  padding: 0,
  fontSize: 14,
  fontWeight: 'bold',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': {
    opacity: 0.85,
  },
})

// 未接続のときは接続操作を促すため、状態の文言だけをリンクと同じオレンジにする
const notConnectedStyle = css({ color: '#F2A40B' })
